import React, { useState, useMemo } from 'react';
import { UnifiedSessionData } from '../types/DataTypes';
import { RouteMap } from './RouteMap';
import { hasGPSData as sharedHasGPSData } from '../utils/dataRichnessHelpers';

interface ExpandableGPSRouteMapProps {
  session: UnifiedSessionData;
  isInitiallyExpanded?: boolean;
}

/**
 * Downsample GPS points for rendering performance.
 * Long activities (2+ hrs at 1Hz) can produce 7000+ points —
 * cap at maxPoints to keep Leaflet responsive.
 */
const downsampleGPSPoints = (
  points: Array<{ latitude: number; longitude: number; timestamp?: any; altitude?: number }>,
  maxPoints = 1000
): Array<{ latitude: number; longitude: number; timestamp: any; altitude?: number }> => {
  if (!points || !Array.isArray(points) || points.length <= maxPoints) return points ?? [];
  const step = Math.ceil(points.length / maxPoints);
  const sampled = points.filter((_, i) => i % step === 0);
  // Always include the last point (finish marker)
  const last = points[points.length - 1];
  if (sampled[sampled.length - 1] !== last) {
    sampled.push(last);
  }
  return sampled;
};

export const ExpandableGPSRouteMap: React.FC<ExpandableGPSRouteMapProps> = ({
  session,
  isInitiallyExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);

  // Check if GPS data is available — use shared helper (validates lat/lng !== undefined, handles 0° coords)
  const hasGPSData = sharedHasGPSData(session);
  const hasSummaryStats = (session.distance != null && session.distance > 0) ||
                          (session.elevationGain != null && session.elevationGain > 0) ||
                          session.avgPace != null;


  // Helper to format pace
  const formatPace = (paceSeconds: number): string => {
    const minutes = Math.floor(paceSeconds / 60);
    const seconds = Math.floor(paceSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}/km`;
  };

  // Downsample GPS points for map rendering performance
  const mapPoints = useMemo(() => {
    if (!session.gpsData || session.gpsData.length === 0) return [];
    return downsampleGPSPoints(session.gpsData);
  }, [session.gpsData]);

  // Don't render if no GPS data.
  //
  // RULES OF HOOKS: this guard MUST stay below the useMemo above. Hoisting
  // it made the hook count depend on whether the session carried GPS —
  // a prop that changes as session data loads.
  if (!hasGPSData && !hasSummaryStats) {
    return null;
  }

  return (
    <div className="mx-4 mb-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between py-3 px-4 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-gray-900">
            📍 Route & Stats
          </span>
          {hasSummaryStats && session.distance && (
            <span className="text-sm text-gray-500">
              {(session.distance / 1000).toFixed(2)} km
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-3 px-4 py-4 bg-white rounded-lg border border-gray-200">
          {/* Interactive Leaflet Route Map */}
          {hasGPSData && mapPoints.length > 0 && (
            <div className="mb-4">
              <RouteMap
                gpsPoints={mapPoints}
                splits={session.splits ?? []}
                height="300px"
                className="rounded-lg overflow-hidden"
              />
              {/* Route Markers Legend */}
              <div className="mt-3 flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center gap-1">
                  <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#16a34a' }}></div>
                  <span>Start</span>
                </div>
                <div className="flex items-center gap-1">
                  <span>{mapPoints.length} trackpoints</span>
                </div>
                <div className="flex items-center gap-1">
                  <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#dc2626' }}></div>
                  <span>Finish</span>
                </div>
              </div>
            </div>
          )}

          {/* Route Stats */}
          <div className="space-y-3">
            {/* Distance */}
            {session.distance != null && session.distance > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Distance</span>
                <span className="text-sm font-semibold text-gray-900">
                  {(session.distance / 1000).toFixed(2)} km
                </span>
              </div>
            )}

            {/* Average Pace */}
            {session.avgPace != null && session.avgPace > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Avg Pace</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatPace(session.avgPace)}
                </span>
              </div>
            )}

            {/* Elevation Gain */}
            {session.elevationGain != null && session.elevationGain > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Elevation Gain</span>
                <span className="text-sm font-semibold text-gray-900">
                  {Math.round(session.elevationGain)}m ↑
                </span>
              </div>
            )}

            {/* Elevation Loss */}
            {session.elevationLoss != null && session.elevationLoss > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Elevation Loss</span>
                <span className="text-sm font-semibold text-gray-900">
                  {Math.round(session.elevationLoss)}m ↓
                </span>
              </div>
            )}

            {/* Total Duration */}
            {session.totalDuration != null && session.totalDuration > 0 && (
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Duration</span>
                <span className="text-sm font-semibold text-gray-900">
                  {Math.round(session.totalDuration / 60)} min
                </span>
              </div>
            )}

            {/* Average Speed */}
            {session.avgSpeed != null && session.avgSpeed > 0 && (
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Avg Speed</span>
                <span className="text-sm font-semibold text-gray-900">
                  {(session.avgSpeed * 3.6).toFixed(1)} km/h
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

